import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  ListItem,
} from "@mui/material";
import {
  IdentityDocumentType,
  Member,
  Membership,
  MembershipStatus,
  MembershipType,
} from "../../../../services/core-api-adapter";
import FaceIconBrand from "../../../../theme/icons/FaceIconBrand";
import FilledStarIcon from "../../../../theme/icons/FilledStarIcon";
import MembershipDependentsStatusModal from "../MembershipDependentsStatusModal";
import MembershipStatusIndicator from "../MembershipStatusIndicator";
import useGetMemberships from "../../../Common/hooks/useGetMemberships";
import theme from "../../../../theme";
import { convertPxToRem, replaceSubjectWithString } from "../../../../utils";

interface MembershipDetailDependentsListProps {
  dependents: Member[];
}

interface MembershipDetailDependentItemProps {
  fullName: string;
  identityDocumentValue: string;
  identityDocumentType: string;
  hasIdentityDocumentTypeAndValue: boolean;
  isMainMember: boolean;
  dependent: Member;
  firstMembership: Membership;
  onMembershipDependentStatusClick: () => void;
}

function MembershipDetailDependentItem({
  fullName,
  identityDocumentValue,
  identityDocumentType,
  hasIdentityDocumentTypeAndValue,
  isMainMember,
  dependent,
  firstMembership,
  onMembershipDependentStatusClick,
}: MembershipDetailDependentItemProps) {
  const { t } = useTranslation();

  return (
    <ListItem
      key={`${fullName}_${dependent.membershipDetails.beneficiaryCode}`}
      aria-label={fullName}
      sx={{ px: 0, py: 2 }}
    >
      <Stack spacing={1} width="100%">
        <Stack spacing={2} direction="row" width="100%">
          <Stack sx={{ position: "relative", height: theme.spacing(4) }}>
            <FaceIconBrand sx={{ fontSize: (theme) => theme.spacing(4) }} />
            {isMainMember && (
              <Stack
                sx={{
                  position: "absolute",
                  bottom: (theme) => theme.spacing(-0.5),
                  right: (theme) => theme.spacing(-0.5),
                }}
              >
                <FilledStarIcon
                  sx={{ fontSize: (theme) => theme.spacing(2) }}
                />
              </Stack>
            )}
          </Stack>
          <Stack spacing={1} width="100%">
            <Stack
              spacing={1}
              direction="row"
              alignContent="flex-start"
              justifyContent="space-between"
              width="100%"
            >
              <Stack>
                <Typography component="p" variant="h4" fontWeight="600">
                  {fullName}
                </Typography>
                {isMainMember && (
                  <Typography component="p" variant="h5" fontWeight="600">
                    {t("AddDependents.summary.mainMemberLabel")}
                  </Typography>
                )}
              </Stack>
              <Stack>
                <MembershipStatusIndicator
                  status={dependent.membershipDetails.status}
                />
              </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Box>
                {hasIdentityDocumentTypeAndValue && (
                  <>
                    <Typography variant="body2" color="GrayText">
                      {t(
                        `MembershipDependentsDetailPanel.dependentsList.item.${identityDocumentType}Label` as any
                      )}
                    </Typography>
                    <Typography>{identityDocumentValue}</Typography>
                  </>
                )}
                {!hasIdentityDocumentTypeAndValue && (
                  <>
                    <Typography variant="body2" color="GrayText">
                      {t("MembershipDetailPanel.dateOfBirthLabel")}
                    </Typography>
                    <Typography>
                      {dependent?.memberDetails?.dateOfBirth
                        ? new Date(
                            dependent.memberDetails.dateOfBirth
                          ).toLocaleDateString()
                        : null}
                    </Typography>
                  </>
                )}
              </Box>

              {dependent.membershipDetails.beneficiaryCode && (
                <Box>
                  <Typography variant="body2" color="GrayText">
                    {t(
                      "MembershipDependentsDetailPanel.dependentsList.item.codeLabel"
                    )}
                  </Typography>
                  <Typography textAlign="right">
                    {dependent.membershipDetails.beneficiaryCode}
                  </Typography>
                </Box>
              )}
            </Stack>

            <Stack spacing={2} direction="row" justifyContent="space-between">
              <Stack>
                {hasIdentityDocumentTypeAndValue && (
                  <>
                    <Typography variant="body2" color="GrayText">
                      {t("MembershipDetailPanel.dateOfBirthLabel")}
                    </Typography>
                    <Typography>
                      {dependent?.memberDetails?.dateOfBirth
                        ? new Date(
                            dependent.memberDetails.dateOfBirth
                          ).toLocaleDateString()
                        : null}
                    </Typography>
                  </>
                )}
              </Stack>

              {firstMembership.productDetails.startDate && (
                <Stack>
                  <Typography
                    variant="body2"
                    color="GrayText"
                    textAlign="right"
                  >
                    {t("MembershipDetailPanel.activationDateLabel")}
                  </Typography>
                  <Typography textAlign="right">
                    {new Date(
                      firstMembership.productDetails.startDate
                    ).toLocaleDateString()}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>

        {dependent.membershipDetails.status === MembershipStatus.PENDING && (
          <Button
            variant="text"
            size="small"
            fullWidth
            onClick={onMembershipDependentStatusClick}
          >
            {t(
              "MembershipDependentsDetailPanel.dependentsList.item.action.membershipPendingButton"
            )}
          </Button>
        )}
      </Stack>
    </ListItem>
  );
}

export default function MembershipDetailDependentsListV2({
  dependents,
}: MembershipDetailDependentsListProps) {
  const { t } = useTranslation();
  const { firstMembership } = useGetMemberships();
  const [
    isMembershipDependentsStatusModalOpen,
    setIsMembershipDependentsStatusModalOpen,
  ] = useState(false);
  const [mainMember, setMainMember] = useState<Member | null>(null);
  const [memberDependents, setMemberDependents] = useState<Member[]>([]);
  const [adultDependent, setAdultDependent] = useState<Member | null>(null);

  useEffect(() => {
    setMemberDependents([]);
    let tempMemberDependents: Member[] = [];
    dependents.forEach((dependent) => {
      if (dependent.membershipDetails.type === MembershipType.MAIN_MEMBER) {
        setMainMember(dependent);
      } else if (
        dependent.membershipDetails.type === MembershipType.DEPENDENT &&
        dependent.isPrimaryCard
      )
        setAdultDependent(dependent);
      else {
        tempMemberDependents.push(dependent);
      }
    });
    setMemberDependents(tempMemberDependents);
  }, [dependents]);

  function onMembershipDependentStatusClick() {
    setIsMembershipDependentsStatusModalOpen(true);
  }

  function getDependentDetails(dependent: Member) {
    const fullName = dependent.memberDetails.memberFullName;
    const identityDocumentValue =
      dependent?.memberDetails?.identityDocumentValue || "";
    const isMainMember =
      dependent.membershipDetails.type === MembershipType.MAIN_MEMBER;
    const identityDocumentType = dependent.memberDetails.identityDocumentType
      ? replaceSubjectWithString(
          dependent.memberDetails.identityDocumentType,
          IdentityDocumentType.UNUID,
          "UNUID"
        )
      : null;
    const hasIdentityDocumentTypeAndValue = Boolean(
      identityDocumentType && identityDocumentValue
    );
    return {
      fullName,
      identityDocumentValue,
      identityDocumentType,
      hasIdentityDocumentTypeAndValue,
      isMainMember,
    };
  }

  return (
    <>
      <Stack spacing={convertPxToRem(16)} mt={convertPxToRem(16)}>
        {adultDependent && (
          <Stack
            aria-label={t("MembershipDependentsDetailPanel.myDetails.title")}
          >
            <Typography variant="h3" color="GrayText">
              {t("MembershipDependentsDetailPanel.myDetails.title")}
            </Typography>
            <MembershipDetailDependentItem
              fullName={adultDependent.memberDetails.memberFullName}
              identityDocumentValue={
                adultDependent.memberDetails.identityDocumentValue || ""
              }
              identityDocumentType={
                adultDependent.memberDetails.identityDocumentType || ""
              }
              hasIdentityDocumentTypeAndValue={Boolean(
                adultDependent.memberDetails.identityDocumentType &&
                  adultDependent.memberDetails.identityDocumentValue
              )}
              isMainMember={false}
              dependent={adultDependent}
              firstMembership={firstMembership}
              onMembershipDependentStatusClick={
                onMembershipDependentStatusClick
              }
            />
            <Divider />
          </Stack>
        )}

        <Stack
          aria-label={
            adultDependent
              ? t("MembershipDependentsDetailPanel.mainMember.title")
              : t("MembershipDependentsDetailPanel.myDetails.title")
          }
        >
          <Typography variant="h3" color="GrayText">
            {adultDependent
              ? t("MembershipDependentsDetailPanel.mainMember.title")
              : t("MembershipDependentsDetailPanel.myDetails.title")}
          </Typography>
          {mainMember && (
            <MembershipDetailDependentItem
              fullName={mainMember.memberDetails.memberFullName}
              identityDocumentValue={
                mainMember.memberDetails.identityDocumentValue || ""
              }
              identityDocumentType={
                mainMember.memberDetails.identityDocumentType || ""
              }
              hasIdentityDocumentTypeAndValue={Boolean(
                mainMember.memberDetails.identityDocumentType &&
                  mainMember.memberDetails.identityDocumentValue
              )}
              isMainMember={true}
              dependent={mainMember}
              firstMembership={firstMembership}
              onMembershipDependentStatusClick={
                onMembershipDependentStatusClick
              }
            />
          )}
          <Divider />
        </Stack>

        {!adultDependent && memberDependents.length > 0 && (
          <Stack
            component="ul"
            aria-label={t(
              "MembershipDependentsDetailPanel.dependentsList.label"
            )}
          >
            <Typography variant="h3" color="GrayText">
              {t("MembershipDependentsDetailPanel.dependentsList.label")}
            </Typography>
            {memberDependents.map((dependent, index) => {
              const {
                fullName,
                identityDocumentValue,
                identityDocumentType,
                hasIdentityDocumentTypeAndValue,
                isMainMember,
              } = getDependentDetails(dependent);
              const key = `${fullName}_${index}`;
              return (
                <Stack key={key}>
                  <MembershipDetailDependentItem
                    fullName={fullName}
                    identityDocumentValue={identityDocumentValue}
                    identityDocumentType={identityDocumentType || ""}
                    hasIdentityDocumentTypeAndValue={
                      hasIdentityDocumentTypeAndValue
                    }
                    isMainMember={isMainMember}
                    dependent={dependent}
                    firstMembership={firstMembership}
                    onMembershipDependentStatusClick={
                      onMembershipDependentStatusClick
                    }
                  />
                  <Divider />
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>

      <MembershipDependentsStatusModal
        isOpen={isMembershipDependentsStatusModalOpen}
        onClose={() => setIsMembershipDependentsStatusModalOpen(false)}
        status={MembershipStatus.PENDING}
      />
    </>
  );
}
